interface DecodedJwt {
  sub?: string;
  token_use?: string;
  exp?: number;
  [claim: string]: unknown;
}

/**
 * Decodes a Cognito JWT's payload without verifying its signature. Signature
 * verification is intentionally NOT done here — this app has no backend of
 * its own, and every existing auth flow in this codebase already forwards
 * the raw Cognito token as a Bearer credential and lets the real backend
 * (which holds the actual verification keys) reject anything invalid,
 * expired, or forged. This just performs a structural sanity check plus a
 * local expiry check so we can fail fast on an obviously bad/expired token
 * before ever using it, consistent with how the rest of the app trusts
 * these tokens.
 */
const decodeCognitoToken = (
  token: string,
  expectedTokenUse: 'id' | 'access',
): DecodedJwt => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Malformed token');
  }

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
      .join(''),
  );
  const payload = JSON.parse(json) as DecodedJwt;

  if (payload.token_use !== expectedTokenUse) {
    throw new Error(`Unexpected token_use: ${String(payload.token_use)}`);
  }

  if (!payload.exp || payload.exp * 1000 <= Date.now()) {
    throw new Error('Token is expired');
  }

  return payload;
};

export default decodeCognitoToken;
